import type { StorageAdapter } from './storage/adapter';
import type { LogRow } from './storage/db';

export const LOG_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

const SAFE_STRING = /^[\w.:@/+-]{1,128}$/;

/**
 * Keep only identifiers, types, codes, numbers and booleans — never canvas content (§14.1).
 * Strings with spaces or longer than 128 chars are dropped.
 */
export function sanitizeLogData(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!data) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'string' && SAFE_STRING.test(v)) out[k] = v;
    else if (Array.isArray(v)) {
      const arr = v.filter((x) => typeof x === 'number' || (typeof x === 'string' && SAFE_STRING.test(x))).slice(0, 50);
      if (arr.length) out[k] = arr;
    }
  }
  return out;
}

export interface LoggerOptions {
  flushMs?: number;
  /** Mirror to console in development. */
  console?: boolean;
}

/** Local diagnostics journal (§14). Nothing is sent anywhere automatically (§14.3). */
export class Logger {
  private buffer: LogRow[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private verboseUntil = 0;
  private verboseTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<(row: LogRow) => void>();

  constructor(
    private readonly storage: StorageAdapter | null,
    private readonly opts: LoggerOptions = {},
  ) {}

  get verbose(): boolean {
    return Date.now() < this.verboseUntil;
  }

  get verboseRemainingMs(): number {
    return Math.max(0, this.verboseUntil - Date.now());
  }

  /** Enable detailed debugging for a limited time; it also ends when the editor closes (§14.1). */
  setVerbose(on: boolean, durationMs = 15 * 60 * 1000): void {
    if (this.verboseTimer) clearTimeout(this.verboseTimer);
    this.verboseTimer = null;
    this.verboseUntil = on ? Date.now() + durationMs : 0;
    if (on) this.verboseTimer = setTimeout(() => this.setVerbose(false), durationMs);
    this.info('diag.verbose', { on });
  }

  onLog(fn: (row: LogRow) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  log(level: LogRow['level'], code: string, data?: Record<string, unknown>, verboseData?: Record<string, unknown>): void {
    const verbose = this.verbose;
    const row: LogRow = {
      ts: Date.now(),
      level,
      code,
      data: verbose ? { ...(data ?? {}), ...(verboseData ?? {}) } : sanitizeLogData(data),
      ...(verbose ? { verbose: true } : {}),
    };
    this.buffer.push(row);
    for (const fn of this.listeners) fn(row);
    if (this.opts.console) {
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.debug;
      fn('[vp]', code, row.data ?? '');
    }
    if (!this.timer) this.timer = setTimeout(() => void this.flush(), this.opts.flushMs ?? 2000);
  }

  debug(code: string, data?: Record<string, unknown>): void {
    this.log('debug', code, data);
  }
  info(code: string, data?: Record<string, unknown>): void {
    this.log('info', code, data);
  }
  warn(code: string, data?: Record<string, unknown>): void {
    this.log('warn', code, data);
  }
  error(code: string, data?: Record<string, unknown>): void {
    this.log('error', code, data);
  }

  async flush(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const rows = this.buffer;
    this.buffer = [];
    if (!rows.length || !this.storage) return;
    try {
      await this.storage.appendLogs(rows);
    } catch {
      // diagnostics must never break the app
    }
  }

  /** Remove entries older than 30 days. */
  async prune(now = Date.now()): Promise<number> {
    if (!this.storage) return 0;
    return this.storage.pruneLogs(now - LOG_RETENTION_MS);
  }
}

/** In-memory performance metrics for the diagnostics panel (§14.4). */
export class Metrics {
  private series = new Map<string, { last: number; avg: number; count: number; max: number }>();

  record(name: string, value: number): void {
    const s = this.series.get(name);
    if (!s) this.series.set(name, { last: value, avg: value, count: 1, max: value });
    else {
      s.last = value;
      s.count++;
      s.avg = s.avg + (value - s.avg) / Math.min(s.count, 50);
      s.max = Math.max(s.max, value);
    }
  }

  get(name: string): { last: number; avg: number; count: number; max: number } | undefined {
    return this.series.get(name);
  }

  all(): Record<string, { last: number; avg: number; count: number; max: number }> {
    return Object.fromEntries(this.series);
  }

  /** Time an async operation. */
  async time<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const t0 = performance.now();
    try {
      return await fn();
    } finally {
      this.record(name, performance.now() - t0);
    }
  }
}
